import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import * as React from 'react';
import { useLayoutEffect, useEffect, useState, useRef, useCallback, useMemo, useReducer } from 'react';
import { canUseDOM } from './dom.js';
import { addGlobalEventListener } from './events.js';
import { setRef, applyState } from './misc.js'; // @ts-ignore Access React v18 hooks using string concatenation in order to
// prevent Webpack from inferring that they are not present in React v17. For
// example, React.useId will raise a compile time error when using React v17,
// but React['use' + 'Id'] will not.

var useReactId = React["use" + "Id"]; // @ts-ignore

var useReactDeferredValue = React["use" + "DeferredValue"]; // @ts-ignore

var useInsertionEffect = React["use" + "InsertionEffect"];
/**
 * `React.useLayoutEffect` that fallbacks to `React.useEffect` on server side.
 */

var useSafeLayoutEffect = canUseDOM ? useLayoutEffect : useEffect;
/**
 * Returns a value that never changes even if the argument is updated.
 * @example
 * function Component({ prop }) {
 *   const initialProp = useInitialValue(prop);
 * }
 */

function useInitialValue(value) {
  var _useState = useState(value),
      _useState2 = _slicedToArray(_useState, 1),
      initialValue = _useState2[0];

  return initialValue;
}
/**
 * Returns a value that is lazily initiated and never changes.
 * @example
 * function Component() {
 *   const set = useLazyValue(() => new Set());
 * }
 */


function useLazyValue(init) {
  var ref = useRef();

  if (ref.current === undefined) {
    ref.current = init();
  }

  return ref.current;
}
/**
 * Creates a `React.RefObject` that is constantly updated with the incoming
 * value.
 * @example
 * function Component({ prop }) {
 *   const propRef = useLiveRef(prop);
 * }
 */


function useLiveRef(value) {
  var ref = useRef(value);
  useSafeLayoutEffect(function () {
    ref.current = value;
  });
  return ref;
}
/**
 * Keeps the reference of the previous value to be used in the render phase.
 */


function usePreviousValue(value) {
  var _useState3 = useState(value),
      _useState4 = _slicedToArray(_useState3, 2),
      previousValue = _useState4[0],
      setPreviousValue = _useState4[1];

  if (value !== previousValue) {
    setPreviousValue(value);
  }

  return previousValue;
}
/**
 * Creates a stable callback function that has access to the latest state and
 * can be used within event handlers and effect callbacks. Throws when used in
 * the render phase.
 * @example
 * function Component(props) {
 *   const onClick = useEvent(props.onClick);
 *   React.useEffect(() => {}, [onClick]);
 * }
 */


function useEvent(callback) {
  var ref = useRef(function () {
    throw new Error("Cannot call an event handler while rendering.");
  });

  if (useInsertionEffect) {
    useInsertionEffect(function () {
      ref.current = callback;
    });
  } else {
    ref.current = callback;
  }

  return useCallback(function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return ref.current == null ? void 0 : ref.current.apply(ref, args);
  }, []);
}
/**
 * Merges React Refs into a single memoized function ref so you can pass it to
 * an element.
 * @example
 * const Component = React.forwardRef((props, ref) => {
 *   const internalRef = React.useRef();
 *   return <div {...props} ref={useForkRef(internalRef, ref)} />;
 * });
 */


function useForkRef() {
  for (var _len2 = arguments.length, refs = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    refs[_key2] = arguments[_key2];
  }

  return useMemo(function () {
    if (!refs.some(Boolean)) return;
    return function (value) {
      refs.forEach(function (ref) {
        setRef(ref, value);
      });
    };
  }, refs);
}
/**
 * Returns the ref element's ID.
 */


function useRefId(ref, deps) {
  var _useState5 = useState(undefined),
      _useState6 = _slicedToArray(_useState5, 2),
      id = _useState6[0],
      setId = _useState6[1];

  useSafeLayoutEffect(function () {
    var _ref$current;

    setId(ref == null ? void 0 : (_ref$current = ref.current) == null ? void 0 : _ref$current.id);
  }, deps);
  return id;
}
/**
 * Generates a unique ID. Uses React's useId if available.
 */


function useId(defaultId) {
  if (useReactId) {
    var reactId = useReactId();
    if (defaultId) return defaultId;
    return reactId;
  }

  var _useState7 = useState(defaultId),
      _useState8 = _slicedToArray(_useState7, 2),
      id = _useState8[0],
      setId = _useState8[1];

  useSafeLayoutEffect(function () {
    if (defaultId || id) return;
    var random = Math.random().toString(36).substr(2, 6);
    setId("id-" + random);
  }, [defaultId, id]);
  return defaultId || id;
}
/**
 * Uses React's useDeferredValue if available.
 */


function useDeferredValue(value) {
  if (useReactDeferredValue) {
    return useReactDeferredValue(value);
  }

  var _useState9 = useState(value),
      _useState10 = _slicedToArray(_useState9, 2),
      deferredValue = _useState10[0],
      setDeferredValue = _useState10[1];

  useEffect(function () {
    var raf = requestAnimationFrame(function () {
      return setDeferredValue(value);
    });
    return function () {
      return cancelAnimationFrame(raf);
    };
  }, [value]);
  return deferredValue;
}
/**
 * Returns the tag name by parsing an element ref and the `as` prop.
 * @example
 * function Component(props) {
 *   const ref = React.useRef();
 *   const tagName = useTagName(ref, "button"); // div
 *   return <div ref={ref} {...props} />;
 * }
 */


function useTagName(ref, type) {
  var _useState11 = useState(function () {
    return stringOrUndefined(type);
  }),
      _useState12 = _slicedToArray(_useState11, 2),
      tagName = _useState12[0],
      setTagName = _useState12[1];

  useSafeLayoutEffect(function () {
    var _ref$current2;

    setTagName((ref == null ? void 0 : (_ref$current2 = ref.current) == null ? void 0 : _ref$current2.tagName.toLowerCase()) || stringOrUndefined(type));
  }, [ref, type]);
  return tagName;
}

function stringOrUndefined(type) {
  if (typeof type === "string") {
    return type;
  }

  return;
}
/**
 * A `React.useEffect` that will not run on the first render.
 */


function useUpdateEffect(effect, deps) {
  var mounted = useRef(false);
  useEffect(function () {
    if (mounted.current) {
      return effect();
    }

    mounted.current = true;
  }, deps);
  useEffect(function () {
    return function () {
      mounted.current = false;
    };
  }, []);
}
/**
 * A `React.useLayoutEffect` that will not run on the first render.
 */


function useUpdateLayoutEffect(effect, deps) {
  var mounted = useRef(false);
  useSafeLayoutEffect(function () {
    if (mounted.current) {
      return effect();
    }

    mounted.current = true;
  }, deps);
  useSafeLayoutEffect(function () {
    return function () {
      mounted.current = false;
    };
  }, []);
}
/**
 * A custom version of `React.useState` that uses the `state` and `setState`
 * arguments. If they're not provided, it will use the internal state.
 */


function useControlledState(defaultState, state, setState) {
  var _useState13 = useState(defaultState),
      _useState14 = _slicedToArray(_useState13, 2),
      localState = _useState14[0],
      setLocalState = _useState14[1];

  var nextState = state !== undefined ? state : localState;
  var stateRef = useLiveRef(state);
  var setStateRef = useLiveRef(setState);
  var nextStateRef = useLiveRef(nextState);
  var setNextState = useCallback(function (prevValue) {
    var setStateProp = setStateRef.current;

    if (setStateProp) {
      if (isSetNextState(setStateProp)) {
        setStateProp(prevValue);
      } else {
        var nextValue = applyState(prevValue, nextStateRef.current);
        nextStateRef.current = nextValue;
        setStateProp(nextValue);
      }
    }

    if (stateRef.current === undefined) {
      setLocalState(prevValue);
    }
  }, []);
  defineSetNextState(setNextState);
  return [nextState, setNextState];
}

var SET_NEXT_STATE = Symbol("setNextState");

function isSetNextState(arg) {
  return arg[SET_NEXT_STATE] === true;
}

function defineSetNextState(arg) {
  if (!isSetNextState(arg)) {
    Object.defineProperty(arg, SET_NEXT_STATE, {
      value: true
    });
  }
}
/**
 * A React hook similar to `useState` and `useReducer`, but with the only
 * purpose of re-rendering the component.
 */


function useForceUpdate() {
  return useReducer(function () {
    return [];
  }, []);
}
/**
 * Returns an event callback similar to `useEvent`, but this also accepts a
 * boolean value, which will be turned into a function.
 */


function useBooleanEvent(booleanOrCallback) {
  return useEvent(typeof booleanOrCallback === "function" ? booleanOrCallback : function () {
    return booleanOrCallback;
  });
}
/**
 * Returns props with an additional `wrapElement` prop.
 */


function useWrapElement(props, callback, deps) {
  if (deps === void 0) {
    deps = [];
  }

  var wrapElement = useCallback(function (element) {
    if (props.wrapElement) {
      element = props.wrapElement(element);
    }

    return callback(element);
  }, [].concat(_toConsumableArray(deps), [props.wrapElement]));
  return _objectSpread(_objectSpread({}, props), {}, {
    wrapElement: wrapElement
  });
}
/**
 * Merges the portalRef prop and returns a `domReady` to be used in the
 * components that use Portal underneath.
 */


function usePortalRef(portalProp, portalRefProp) {
  if (portalProp === void 0) {
    portalProp = false;
  }

  var _useState15 = useState(null),
      _useState16 = _slicedToArray(_useState15, 2),
      portalNode = _useState16[0],
      setPortalNode = _useState16[1];

  var portalRef = useForkRef(setPortalNode, portalRefProp);
  var domReady = !portalProp || portalNode;
  return {
    portalRef: portalRef,
    portalNode: portalNode,
    domReady: domReady
  };
}
/**
 * Returns a function that checks whether the mouse is moving.
 */


function useIsMouseMoving() {
  useEffect(function () {
    // We're not returning the event listener cleanup function here because we
    // may lose some events if this component is unmounted, but others are
    // still mounted.
    addGlobalEventListener("mousemove", setMouseMoving, true); // See https://github.com/ariakit/ariakit/issues/1137

    addGlobalEventListener("mousedown", resetMouseMoving, true);
    addGlobalEventListener("mouseup", resetMouseMoving, true);
    addGlobalEventListener("keydown", resetMouseMoving, true);
    addGlobalEventListener("scroll", resetMouseMoving, true);
  }, []);
  var isMouseMoving = useEvent(function () {
    return mouseMoving;
  });
  return isMouseMoving;
}

var mouseMoving = false;
var previousScreenX = 0;
var previousScreenY = 0;

function hasMouseMovement(event) {
  var movementX = event.movementX || event.screenX - previousScreenX;
  var movementY = event.movementY || event.screenY - previousScreenY;
  previousScreenX = event.screenX;
  previousScreenY = event.screenY;
  return movementX || movementY || process.env.NODE_ENV === "test";
}

function setMouseMoving(event) {
  if (!hasMouseMovement(event)) return;
  mouseMoving = true;
}

function resetMouseMoving() {
  mouseMoving = false;
}

export { useBooleanEvent, useControlledState, useDeferredValue, useEvent, useForceUpdate, useForkRef, useId, useInitialValue, useIsMouseMoving, useLazyValue, useLiveRef, usePortalRef, usePreviousValue, useRefId, useSafeLayoutEffect, useTagName, useUpdateEffect, useUpdateLayoutEffect, useWrapElement };